// frontend/src/utils/authFetch.js
import { Capacitor } from "@capacitor/core";
const API_URL = process.env.REACT_APP_API_URL || "";

let refreshPromise = null;

function buildUrl(pathOrUrl) {
  let url = pathOrUrl || "";

  if (!/^https?:\/\//i.test(url)) {
    if (!url.startsWith("/")) url = `/${url}`;
    url = `${API_URL}${url}`;
  }

  return url;
}

function clearAuth() {
  localStorage.removeItem("access");
  localStorage.removeItem("refresh");
  localStorage.removeItem("me.username");
  localStorage.removeItem("me.role");
  localStorage.removeItem("me.store_name");
  localStorage.removeItem("session_only");

  sessionStorage.removeItem("access");
  sessionStorage.removeItem("refresh");

  window.dispatchEvent(new Event("localstorage-auth"));
}

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refresh =
      localStorage.getItem("refresh") ||
      sessionStorage.getItem("refresh");

    if (!refresh) {
      throw new Error("Refresh token assente.");
    }

    const response = await fetch(`${API_URL}/api/token/refresh/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh }),
    });

    if (!response.ok) {
      throw new Error("Refresh token non valido o scaduto.");
    }

    const data = await response.json();

    if (!data?.access) {
      throw new Error("Nuovo access token non ricevuto.");
    }

    localStorage.setItem("access", data.access);
    sessionStorage.setItem("access", data.access);

    if (data.refresh) {
      localStorage.setItem("refresh", data.refresh);
      sessionStorage.setItem("refresh", data.refresh);
    }

    window.dispatchEvent(new Event("localstorage-auth"));

    return data.access;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Esegue una richiesta autenticata.
 *
 * In caso di 401:
 * 1. prova a rinnovare automaticamente l'access token;
 * 2. ripete una sola volta la richiesta originale;
 * 3. esegue il logout soltanto se il refresh fallisce.
 */
export async function authFetch(pathOrUrl, options = {}) {
  const url = buildUrl(pathOrUrl);

  const prepareRequest = (accessToken) => {
    const headers = new Headers(options.headers || {});
    let body = options.body;

    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    if (options.json !== undefined) {
      headers.set("Content-Type", "application/json");
      body = JSON.stringify(options.json);
    } else if (body && !(body instanceof FormData)) {
      if (typeof body === "object") {
        headers.set("Content-Type", "application/json");
        body = JSON.stringify(body);
      } else if (
        typeof body === "string" &&
        !headers.has("Content-Type")
      ) {
        headers.set("Content-Type", "application/json");
      }
    }

    return {
      ...options,
      headers,
      body,
    };
  };

  const access =
    localStorage.getItem("access") ||
    sessionStorage.getItem("access");

  let response = await fetch(url, prepareRequest(access));

  if (response.status !== 401) {
    return response;
  }

  try {
    const newAccess = await refreshAccessToken();
    response = await fetch(url, prepareRequest(newAccess));

    if (response.status !== 401) {
      return response;
    }
  } catch (error) {
    console.warn("Refresh automatico fallito:", error);
  }

  clearAuth();

  const currentPath = window.location.pathname || "";

  const isNativeApp = Capacitor.isNativePlatform();

  window.location.replace(
    isNativeApp ||
    currentPath === "/app" ||
    currentPath.startsWith("/app/")
      ? "/app/login"
      : "/login"
  );

  throw new Error("Sessione scaduta. Effettua nuovamente l'accesso.");
}

export { API_URL };
